// UserPicker.tsx

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Typography,
  Box,
  ListItemIcon,
  Checkbox,
  ListItemButton,
  Tabs,
  Tab
} from "@mui/material";

/**
 * Basic interface for a user object.
 */
export interface GraphUser {
  id: string;                  // AAD objectId
  displayName: string;
  userPrincipalName?: string;
}

/**
 * The props for our UserPicker component:
 */
interface UserPickerProps {
  open: boolean;                           // Dialog open?
  onClose: () => void;                     // Cancel
  onSubmit: (selectedUsers: GraphUser[]) => void; // Confirm selection
  graphAccessToken: string;                // Must have User.Read.All or Directory.Read.All
  multiSelect?: boolean;                   // Do we allow multiple user picks?
}

const UserPicker: React.FC<UserPickerProps> = ({
  open,
  onClose,
  onSubmit,
  graphAccessToken,
  multiSelect = false
}) => {
  // We'll store the full list of users
  const [users, setUsers] = useState<GraphUser[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");

  // If multiSelect = false, we only track single selection. If true, we track a map.
  // Let's do the map approach for consistency:
  const [checkedMap, setCheckedMap] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!open) return;

    // Clear old data
    setUsers([]);
    setError("");
    setSearchQuery("");
    setCheckedMap({});
    setLoading(true);

    fetchUsers().finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /**
   * We do a simple call to /users or /me/people (depending on your scenario).
   * - /users can list the entire tenant's users (requires Directory.Read.All).
   * - You might want to implement a search approach, e.g. /users?$search="displayName:John"
   */
  const fetchUsers = async () => {
    try {
      const url = "https://graph.microsoft.com/v1.0/users?$select=id,displayName,userPrincipalName&$top=50";
      // You can tweak $filter, $search, or $top as needed

      const resp = await fetch(url, {
        headers: { Authorization: `Bearer ${graphAccessToken}` },
      });
      if (!resp.ok) {
        throw new Error(`Failed to fetch users: ${resp.status} ${resp.statusText}`);
      }

      const json = await resp.json();
      if (!json.value) {
        throw new Error("No users found or response invalid.");
      }

      const userList: GraphUser[] = json.value.map((u: any) => ({
        id: u.id,
        displayName: u.displayName,
        userPrincipalName: u.userPrincipalName
      }));

      setUsers(userList);
    } catch (err: any) {
      console.error("Error fetching users:", err);
      setError(err.message || "Unknown error");
    }
  };

  // Filter based on searchQuery
  const getDisplayedList = (): GraphUser[] => {
    if (!searchQuery.trim()) return users;
    const lower = searchQuery.toLowerCase();
    return users.filter(
      (u) =>
        (u.displayName || "").toLowerCase().includes(lower) ||
        (u.userPrincipalName || "").toLowerCase().includes(lower)
    );
  };

  const handleCheck = (userId: string) => {
    if (!multiSelect) {
      // single-select mode => clear the map, set only userId = true
      setCheckedMap({ [userId]: true });
      return;
    }
    // multi-select => toggle
    setCheckedMap((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  const handleSubmit = () => {
    const selected = users.filter((u) => checkedMap[u.id]);
    onSubmit(selected);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Pick User</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ textAlign: "center", my: 3 }}>
            <CircularProgress />
            <Typography>Loading users...</Typography>
          </Box>
        ) : error ? (
          <Typography color="error">Error: {error}</Typography>
        ) : (
          <>
            <TextField
              label="Search Users"
              variant="outlined"
              fullWidth
              sx={{ mb: 2 }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            <List dense sx={{ maxHeight: 400, overflow: "auto", border: "1px solid #ccc" }}>
              {getDisplayedList().map((user) => (
                <ListItem key={user.id} disablePadding>
                  <ListItemButton onClick={() => handleCheck(user.id)}>
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={!!checkedMap[user.id]}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={user.displayName || "(no name)"}
                      secondary={user.userPrincipalName}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserPicker;
